import React from "react"

import type { IconType } from "@icons-pack/react-simple-icons"
import { twMerge } from "tailwind-merge"

import type { LucideIcon } from "wealthmode-frontend/icons"

/** Theme base body text (`global.css` html/body). Used when no `text-*` color class is provided. */
const DEFAULT_TEXT_COLOR_CLASS = "text-foreground-primary"

type AdaptedIconProps = {
  size?: number | string
  width?: number | string
  height?: number | string
  /** Explicit color override; bypasses className-driven coloring. */
  color?: string
  className?: string
  stroke?: string
  fill?: string
  /** React Native Web encodes className as `{ $$css: true, [classNames]: classNames }` here. */
  style?: React.CSSProperties | { $$css: true; [key: string]: string | true }
}

/** Recover the class string RN-Web stashes inside `style.$$css`. */
function extractRNWClassName(style: AdaptedIconProps["style"]): string | undefined {
  if (style && typeof style === "object" && "$$css" in style && style.$$css === true) {
    const keys = Object.keys(style).filter((key) => key !== "$$css")

    return keys.join(" ") || undefined
  }

  return undefined
}

/**
 * Gluestack Icon's base class adds `fill-none` (for stroke-based Lucide icons).
 * Simple Icons are fill-based, so the class would zero out the fill.
 * Likewise `pointer-events-none` is harmless but irrelevant here.
 */
function stripIncompatibleIconClasses(className: string): string {
  return className
    .replace(/\bfill-none\b/g, "")
    .replace(/\bpointer-events-none\b/g, "")
    .replace(/\s+/g, " ")
    .trim()
}

function resolveClassName(className: string | undefined, style: AdaptedIconProps["style"]): string {
  const fromStyle = extractRNWClassName(style)
  const merged = twMerge(className ?? "", fromStyle ?? "")

  return twMerge(DEFAULT_TEXT_COLOR_CLASS, stripIncompatibleIconClasses(merged))
}

function resolvePixelSize(size?: number | string, width?: number | string, height?: number | string): number {
  for (const value of [size, width, height]) {
    if (typeof value === "number") {
      return value
    }

    if (typeof value === "string") {
      const parsed = Number.parseFloat(value)
      if (!Number.isNaN(parsed)) {
        return parsed
      }
    }
  }

  return 24
}

function resolveColor(props: AdaptedIconProps): string {
  const { color, fill, stroke } = props

  if (fill && fill !== "none") {
    return fill
  }

  if (color) {
    return color
  }

  if (stroke && stroke !== "currentColor") {
    return stroke
  }

  // Defer to CSS via the className (`color: …` cascaded through `fill="currentColor"`).
  return "currentColor"
}

/** Wraps a Simple Icons component so it accepts gluestack/Lucide-style sizing and color props. */
export function adaptSimpleIcon(SimpleIcon: IconType): LucideIcon {
  const Wrapped = React.forwardRef<SVGSVGElement, AdaptedIconProps>(function AdaptedSimpleIcon(
    { size, width, height, className, style, ...colorProps },
    ref,
  ) {
    const resolvedSize = resolvePixelSize(size, width, height)
    const mergedClassName = resolveClassName(className, style)
    const fillColor = resolveColor(colorProps)

    return (
      <SimpleIcon
        ref={ref}
        size={resolvedSize}
        width={resolvedSize}
        height={resolvedSize}
        color={fillColor}
        className={mergedClassName}
      />
    )
  })

  Wrapped.displayName = `Adapted(${SimpleIcon.displayName ?? "SimpleIcon"})`

  return Wrapped as unknown as LucideIcon
}
